<template>
  <v-container class="itinerary-admin">
    <div class="page-header">
      <h2>IABF {{ adminFestivalYear }} — Itinerary Rooms</h2>
      <div>
        <v-btn outlined :to="{ path: '/iabf/admin/class-descriptions' }">Edit descriptions →</v-btn>
        <v-btn outlined class="ml-2" :to="{ path: '/itinerary' }">Dancer view →</v-btn>
      </div>
    </div>
    <p class="muted">Click any class to edit. Use "+ Add class" for a new slot. Room dropdown on each cell saves instantly without opening the editor.</p>

    <div class="publish-bar" :class="{ 'is-published': publishStatus === true }">
      <div class="publish-status">
        <span class="publish-dot"></span>
        <span class="publish-label">
          {{ publishStatus === null ? 'Checking…' : publishStatus ? 'Published — visible to dancers' : 'Hidden — dancers see "Schedule coming soon"' }}
        </span>
      </div>
      <v-btn small :color="publishStatus ? 'default' : 'primary'" :loading="publishSaving" @click="togglePublish">
        {{ publishStatus ? 'Unpublish' : 'Publish to dancers' }}
      </v-btn>
    </div>

    <v-expansion-panels flat class="teacher-links-panel">
      <v-expansion-panel>
        <v-expansion-panel-header>
          <span class="teacher-links-header">Teacher edit links ({{ teacherList.length }})</span>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <p class="muted teacher-links-hint">Each teacher gets a personal URL where they can edit only their own class titles, notes, and descriptions. Send each one their link.</p>
          <div class="teacher-link-row" v-for="t in teacherList" :key="t">
            <span class="teacher-link-name">{{ t }}</span>
            <code class="teacher-link-url">{{ teacherLinkFor(t) }}</code>
            <v-btn x-small outlined @click="copyTeacherLink(t)">
              {{ copiedTeacher === t ? '✓ Copied' : 'Copy' }}
            </v-btn>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-tabs v-model="activeDay" class="day-tabs" background-color="transparent">
      <v-tab v-for="d in daysWithClasses" :key="d.key" :href="`#${d.key}`">{{ d.label }}</v-tab>
    </v-tabs>

    <div class="day-actions">
      <v-btn color="primary" small @click="openNewClass">+ Add class to {{ activeDayLabel }}</v-btn>
      <v-btn small outlined class="ml-2" @click="copyDaySchedule">
        {{ copyFlash ? '✓ Copied' : `Copy ${activeDayLabel}` }}
      </v-btn>
      <v-btn small outlined class="ml-2" @click="copyPrivateLessons">
        {{ copyPrivateFlash ? '✓ Copied' : 'Copy private lessons' }}
      </v-btn>
      <v-btn small outlined class="ml-2" @click="copyTimeLabels">
        {{ copyTimesFlash ? '✓ Copied' : `Copy ${activeDayLabel} times` }}
      </v-btn>
      <v-menu offset-y :close-on-content-click="false">
        <template #activator="{ on, attrs }">
          <v-btn small outlined class="ml-2" v-bind="attrs" v-on="on">
            Copy descriptions ▾
          </v-btn>
        </template>
        <div class="desc-menu">
          <div class="desc-menu-hint">Pick a teacher to copy their {{ activeDayLabel }} classes &amp; descriptions.</div>
          <button
            v-for="t in teachersForDay"
            :key="t"
            type="button"
            class="desc-menu-item"
            @click="copyTeacherDescriptions(t)"
          >
            <span>{{ t }}</span>
            <span v-if="copyDescriptionsFlash === t" class="desc-menu-flash">✓ Copied</span>
          </button>
          <div v-if="teachersForDay.length === 0" class="desc-menu-empty">No teachers scheduled on {{ activeDayLabel }}.</div>
        </div>
      </v-menu>
    </div>

    <div v-if="teachersForDay.length > 0" class="teacher-filter">
      <span class="filter-label">Filter by teacher:</span>
      <button type="button" class="teacher-chip" :class="{ active: !selectedTeacher }" @click="selectedTeacher = ''">All</button>
      <button v-for="t in teachersForDay" :key="t" type="button" class="teacher-chip" :class="{ active: selectedTeacher === t }" @click="selectedTeacher = t">{{ t }}</button>
    </div>

    <div v-if="loading" class="muted pad-y">Loading…</div>
    <div v-else>
      <div v-for="d in daysWithClasses" :key="d.key" v-show="activeDay === d.key">
        <!-- Grid view (no teacher filter) -->
        <table v-if="!selectedTeacher" class="itin-table">
          <thead>
            <tr>
              <th class="time-col">Time</th>
              <th v-for="t in tracksForDay(d.key)" :key="t">{{ TRACK_LABEL[t] }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="slot in timeSlotsForDay(d.key)" :key="slot">
              <td class="time-cell">{{ slot }}</td>
              <td v-for="cell in rowCells(d.key, slot)" :key="cell.key" class="class-cell" :class="{ 'all-cell': cell.isAll }">
                <span v-if="cell.classes.length === 0" class="muted small">—</span>
                <template v-else>
                  <div v-for="(item, ci) in cell.classes" :key="item.id" :class="{ 'stack-item': ci > 0 }">
                    <ClassCell
                      :cls="item"
                      :room-options="ROOM_OPTIONS"
                      :rooms-taken="roomsTakenInRow(d.key, slot, item.id)"
                      :dance-studio-rooms="DANCE_STUDIO_ROOMS"
                      :room-conflict="rowRoomConflicts(d.key, slot).has((item.room || item.defaultRoom || '').trim())"
                      @update="saveRoom"
                      @accompanist="saveAccompanist"
                      @edit="openEditor"
                    />
                  </div>
                </template>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Single-column teacher view -->
        <div v-else class="teacher-column">
          <div v-if="teacherClassesForDay(d.key).length === 0" class="muted pad-y">
            {{ selectedTeacher }} has no classes on {{ d.label }}.
          </div>
          <div v-else class="teacher-class-list">
            <div v-for="c in teacherClassesForDay(d.key)" :key="c.id" class="teacher-class-row">
              <div class="teacher-class-time">{{ c.timeLabel || '?' }}</div>
              <div class="teacher-class-card">
                <ClassCell
                  :cls="c"
                  :room-options="ROOM_OPTIONS"
                  :rooms-taken="roomsTakenInRow(d.key, c.timeLabel, c.id)"
                  :dance-studio-rooms="DANCE_STUDIO_ROOMS"
                  @update="saveRoom"
                  @accompanist="saveAccompanist"
                  @edit="openEditor"
                />
              </div>
            </div>
          </div>
        </div>

        <div v-if="!selectedTeacher && orphanedClassesForDay(d.key).length > 0" class="orphan-panel">
          <div class="orphan-header">⚠ Classes not shown in any track column</div>
          <div class="orphan-hint">These classes are on {{ d.label }} but their track is empty or no longer recognized. Edit them to pick a track.</div>
          <table class="orphan-table">
            <tbody>
              <tr v-for="c in orphanedClassesForDay(d.key)" :key="c.id" @click="openEditor(c)" class="orphan-row">
                <td class="orphan-time">{{ c.timeLabel || '?' }}</td>
                <td class="orphan-title">{{ c.title || '(untitled)' }}</td>
                <td class="orphan-teacher">{{ c.teacher || '' }}</td>
                <td class="orphan-tracks muted">tracks: {{ (c.tracks && c.tracks.length ? c.tracks.join(', ') : (c.track || 'none')) }}</td>
                <td><v-btn x-small outlined @click.stop="openEditor(c)">Edit</v-btn></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <v-dialog v-model="editorOpen" max-width="640" scrollable>
      <v-card>
        <v-card-title>{{ editorIsNew ? 'Add class' : 'Edit class' }}</v-card-title>
        <v-card-text>
          <div class="form-grid">
            <div class="form-row">
              <label class="form-lab">Day</label>
              <select v-model="editor.day" class="form-input">
                <option v-for="d in ALL_DAYS" :key="d.key" :value="d.key">{{ d.label }}</option>
              </select>
            </div>

            <div class="form-row">
              <label class="form-lab">Time label</label>
              <input v-model="editor.timeLabel" placeholder="e.g. 9-10:45" class="form-input" />
              <p class="form-hint">Shown in the Time column. Free text.</p>
            </div>

            <div class="form-cols">
              <div class="form-row">
                <label class="form-lab">Start time</label>
                <input v-model="editor.startTimeDisplay" placeholder="9:00 AM" class="form-input" @blur="editor.startTimeDisplay = normalize12h(editor.startTimeDisplay)" />
                <p class="form-hint">Used to sort the day. Required.</p>
              </div>
              <div class="form-row">
                <label class="form-lab">End time</label>
                <input v-model="editor.endTimeDisplay" placeholder="10:45 AM" class="form-input" @blur="editor.endTimeDisplay = normalize12h(editor.endTimeDisplay)" />
                <p class="form-hint">When two classes start at the same time, the longer one is shown first.</p>
              </div>
            </div>

            <div class="form-row">
              <label class="form-lab">Tracks (columns this class lives in)</label>
              <div class="track-checks">
                <label v-for="t in EDIT_TRACK_CHOICES" :key="t.key" class="track-check">
                  <input type="checkbox" :checked="editor.tracks.includes(t.key)" @change="toggleEditorTrack(t.key)" />
                  {{ t.label }}
                </label>
              </div>
              <p class="form-hint">Pick one or more. Use "All" for breakfast/lunch/dinner-style rows that span every column.</p>
            </div>

            <div class="form-row">
              <label class="form-lab">Title</label>
              <input v-model="editor.title" placeholder="Class title" class="form-input" />
            </div>

            <div class="form-cols">
              <div class="form-row">
                <label class="form-lab">Teacher(s)</label>
                <input v-model="editor.teacher" placeholder="e.g. Sean Kelly" class="form-input" />
                <p class="form-hint">Comma-separated for multiple (e.g. "Sean Kelly, Mikey Cusumano"). Tick a chip below to add a known teacher.</p>
                <div class="teacher-picker">
                  <label v-for="t in knownTeachers" :key="t" class="teacher-pick-chip" :class="{ active: editorTeachers.includes(t) }">
                    <input type="checkbox" :checked="editorTeachers.includes(t)" @change="toggleEditorTeacher(t)" />
                    {{ t }}
                  </label>
                </div>
              </div>
              <div class="form-row">
                <label class="form-lab">Default room (from booklet)</label>
                <select v-model="editor.defaultRoom" class="form-input">
                  <option value="">— none —</option>
                  <option v-for="r in ROOM_OPTIONS" :key="r" :value="r">{{ r }}</option>
                </select>
              </div>
            </div>

            <div v-if="editorRoomIsDanceStudio" class="form-row">
              <label class="form-check">
                <input type="checkbox" v-model="editor.hasAccompanist" />
                🎹 Live piano accompanist
              </label>
              <p class="form-hint">Shows a 🎹 chip on the dancer schedule for this class.</p>
            </div>

            <div class="form-row">
              <label class="form-lab">Notes (small line under teacher)</label>
              <input v-model="editor.notes" placeholder="e.g. 9-10:45 + 15 mins Q&amp;A" class="form-input" />
              <p class="form-hint">For logistics, time blocks, etc. Don't put accompanist info here — use the checkbox above.</p>
            </div>

            <div class="form-row">
              <label class="form-lab">Description (shown on dancer page when Details is expanded)</label>
              <textarea v-model="editor.description" rows="4" class="form-input form-textarea" placeholder="Optional class description." />
            </div>

            <p v-if="editorError" class="error--text">{{ editorError }}</p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn v-if="!editorIsNew" color="error" text :loading="editorDeleting" @click="confirmDelete">Delete class</v-btn>
          <v-spacer />
          <v-btn text @click="editorOpen = false" :disabled="editorSaving">Cancel</v-btn>
          <v-btn color="primary" dark :loading="editorSaving" @click="saveEditor">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { db } from '@/firebase'
import iabfAdminMixin from '@/utils/iabf-admin-mixin'


const TRACK_LABEL = {
  rehearsal:  'Rehearsal',
  begBallet:  'Beg. Ballet',
  intBallet:  'Int Ballet',
  contemp:    'Contemporary',
  all:        'All Tracks'
}

// Rooms where live piano accompaniment is a sensible option. Used to
// gate the Accompanist checkbox in the editor — anywhere else, the box
// is hidden so we don't suggest live piano in a library or amphitheater.
// Faculty on the roster who may not be assigned to any class yet. Merged
// into the auto-derived teacher list so they're still suggested in the
// editor autocomplete and get a personal teacher edit link.
const EXTRA_FACULTY = [
  'Krystal Hout',
  'Madame Olga'
]

const DANCE_STUDIO_ROOMS = new Set([
  'Harris Theater',
  'Roberson Dance Studio'
])

const ROOM_OPTIONS = [
  'Harris Theater',
  'Harris Theater Lobby',
  'Roberson Dance Studio',
  'Pabst Visitor Center & Gallery',
  'Everbach Painting Studio',
  'Hubbard Music Studio',
  'Urban Amphitheater',
  'Thompson Field House',
  'Wilkinson Library',
  "Writer's Studio",
  'Pugh Sculpture Studio'
]

// Display labels drop the donor-name prefix — the room dropdown was hard to
// scan with all the surnames. Stored value stays the full string; this is
// display-only.
const ROOM_SHORT = {
  'Harris Theater': 'Theater',
  'Harris Theater Lobby': 'Theater Lobby',
  'Roberson Dance Studio': 'Dance Studio',
  'Pabst Visitor Center & Gallery': 'Visitor Center & Gallery',
  'Everbach Painting Studio': 'Painting Studio',
  'Hubbard Music Studio': 'Music Studio',
  'Urban Amphitheater': 'Amphitheater',
  'Thompson Field House': 'Field House',
  'Wilkinson Library': 'Library',
  'Pugh Sculpture Studio': 'Sculpture Studio'
}
function shortRoom (r) { return ROOM_SHORT[r] || r || '' }

const DAY_LABEL = {
  tuesday:   'Tuesday',
  wednesday: 'Wednesday',
  thursday:  'Thursday',
  friday:    'Friday'
}
const DAY_ORDER = { tuesday: 2, wednesday: 3, thursday: 4, friday: 5 }
const ALL_DAYS = Object.keys(DAY_LABEL).map(k => ({ key: k, label: DAY_LABEL[k] }))

const EDIT_TRACK_CHOICES = [
  { key: 'all',        label: 'All Tracks (spans every column)' },
  { key: 'rehearsal',  label: 'Rehearsal' },
  { key: 'begBallet',  label: 'Beg. Ballet' },
  { key: 'intBallet',  label: 'Int Ballet' },
  { key: 'contemp',    label: 'Contemporary' }
]

function emptyEditor () {
  return {
    id: null,
    day: 'tuesday',
    dayOrder: DAY_ORDER.tuesday,
    timeLabel: '',
    startTime: '',
    endTime: '',
    startTimeDisplay: '',
    endTimeDisplay: '',
    order: 100,
    tracks: [],
    title: '',
    teacher: '',
    defaultRoom: '',
    hasAccompanist: false,
    notes: '',
    description: ''
  }
}

// Convert between human 12h ("5:30 PM") and canonical 24h ("17:30") so
// the admin sees friendly times while the Firestore doc keeps a sortable
// 24h string. Returns '' on unparseable input.
function parseTo24h (raw) {
  const s = String(raw || '').trim().toLowerCase().replace(/\s+/g, ' ')
  if (!s) return ''
  // 12h with optional minutes + am/pm/a/p (with or without dot)
  const m12 = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(a|am|p|pm|a\.m\.|p\.m\.)$/)
  if (m12) {
    let h = parseInt(m12[1], 10)
    const min = m12[2] ? parseInt(m12[2], 10) : 0
    const isPm = m12[3].startsWith('p')
    if (h === 12) h = 0
    if (isPm) h += 12
    if (h > 23 || min > 59) return ''
    return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`
  }
  // Already 24h-ish
  const m24 = s.match(/^(\d{1,2}):(\d{2})$/)
  if (m24) {
    const h = parseInt(m24[1], 10)
    const min = parseInt(m24[2], 10)
    if (h > 23 || min > 59) return ''
    return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`
  }
  return ''
}

// Pull H[:M]-H[:M] minute counts out of a free-text time label like
// "9-9:45" or "1-2:15". Used only for ordering. Labels are 12-hour: nothing
// at IABF starts before ~7 AM, so hours 1-6 are always PM (add 12).
function parseSlotRange (label) {
  const m = String(label || '').match(/(\d{1,2})(?::(\d{2}))?\s*-\s*(\d{1,2})(?::(\d{2}))?/)
  if (!m) return { start: 0, end: 0 }
  const toPm = (h) => (h >= 1 && h <= 6) ? h + 12 : h
  const sh = toPm(parseInt(m[1], 10))
  const sm = m[2] ? parseInt(m[2], 10) : 0
  const eh = toPm(parseInt(m[3], 10))
  const em = m[4] ? parseInt(m[4], 10) : 0
  return { start: sh * 60 + sm, end: eh * 60 + em }
}

// Minutes-from-midnight for "HH:MM" canonical times.
function parse24h (s) {
  const m = String(s || '').match(/^(\d{1,2}):(\d{2})$/)
  if (!m) return null
  const h = parseInt(m[1], 10)
  const min = parseInt(m[2], 10)
  if (h > 23 || min > 59) return null
  return h * 60 + min
}

// Sort key for a class. Prefers stored canonical startTime/endTime (most
// reliable) and falls back to parsing the timeLabel for legacy docs.
function classSortKey (c) {
  const startCanon = parse24h(c.startTime || '')
  const endCanon = parse24h(c.endTime || '')
  if (startCanon !== null) {
    return { start: startCanon, end: endCanon !== null ? endCanon : startCanon }
  }
  const fromLabel = parseSlotRange(c.timeLabel || '')
  // Heuristic: if the label looks PM-ish based on order (e.g. "1-2" comes
  // after morning slots), bump it. Without an AM/PM marker, we trust the
  // existing order field for legacy docs.
  return { start: fromLabel.start || (c.order || 0), end: fromLabel.end || 0 }
}

function format12h (canonical) {
  const s = String(canonical || '').trim()
  if (!s) return ''
  const m = s.match(/^(\d{1,2}):(\d{2})$/)
  if (!m) return s
  let h = parseInt(m[1], 10)
  const min = m[2]
  const ap = h >= 12 ? 'PM' : 'AM'
  if (h === 0) h = 12
  else if (h > 12) h -= 12
  return `${h}:${min} ${ap}`
}

const ClassCell = {
  name: 'ClassCell',
  props: ['cls', 'roomOptions', 'roomsTaken', 'roomConflict', 'danceStudioRooms'],
  data () {
    return { customMode: false, customValue: '', menuOpen: false }
  },
  methods: {
    onSelect (val) {
      this.menuOpen = false
      if (val === '__custom__') {
        this.customMode = true
        this.customValue = (this.cls && this.cls.room) || ''
      } else {
        this.$emit('update', { id: this.cls.id, room: val || '' })
      }
    },
    commitCustom () {
      this.$emit('update', { id: this.cls.id, room: (this.customValue || '').trim() })
      this.customMode = false
    },
    toggleMenu () {
      this.menuOpen = !this.menuOpen
    }
  },
  render (h) {
    if (!this.cls) return h('span', { class: 'muted small' }, '—')
    const c = this.cls
    const lines = [
      h('button', {
        class: 'cls-title cls-title-btn',
        attrs: { type: 'button', title: 'Edit class' },
        on: { click: () => this.$emit('edit', c) }
      }, [c.title || '(untitled)', h('span', { class: 'cls-edit-pencil' }, ' ✎')])
    ]
    if (c.teacher) lines.push(h('div', { class: 'cls-teacher' }, c.teacher))
    if (c.notes) lines.push(h('div', { class: 'cls-notes' }, c.notes))
    const isAll = c.track === 'all'
    if (!isAll) {
      if (this.customMode) {
        lines.push(h('div', { class: 'cls-room-row' }, [
          h('input', {
            class: 'cls-room-input',
            domProps: { value: this.customValue },
            on: {
              input: (e) => { this.customValue = e.target.value },
              blur: this.commitCustom,
              keydown: (e) => { if (e.key === 'Enter') this.commitCustom() }
            }
          }),
          h('button', { class: 'cls-room-x', on: { click: () => { this.customMode = false } } }, '×')
        ]))
      } else {
        const label = shortRoom(this.cls.room) || '— pick a room —'
        const btnStyle = {
          width: '100%',
          padding: '4px 22px 4px 6px',
          border: this.roomConflict ? '1px solid #c62828' : (this.cls.room ? '1px solid #388b9d' : '1px solid #ccc'),
          borderRadius: '4px',
          fontSize: '13px',
          fontFamily: "'Fanwood Text', Georgia, serif",
          background: this.roomConflict ? '#fff5f5' : '#fff',
          textAlign: 'center',
          cursor: 'pointer',
          position: 'relative',
          color: this.cls.room ? '#1a4452' : '#555',
          fontWeight: this.cls.room ? '700' : '400'
        }
        const wrapStyle = { position: 'relative' }
        const menuStyle = {
          position: 'absolute',
          top: '100%',
          left: '0',
          right: '0',
          marginTop: '2px',
          background: '#fff',
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          zIndex: '10',
          maxHeight: '280px',
          overflowY: 'auto'
        }
        const itemStyle = {
          display: 'block',
          width: '100%',
          padding: '6px 10px',
          background: 'transparent',
          border: 'none',
          textAlign: 'center',
          fontFamily: "'Fanwood Text', Georgia, serif",
          fontSize: '13px',
          color: '#1a1a1a',
          cursor: 'pointer'
        }
        const selectedItemStyle = { ...itemStyle, background: '#eef6f8', fontWeight: '700', color: '#1a4452' }
        const customItemStyle = { ...itemStyle, borderTop: '1px solid #eee', fontStyle: 'italic' }
        const chevStyle = { position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', color: '#888', fontSize: '10px' }
        // Hide rooms already taken by other classes in this time slot so
        // the admin doesn't have to remember who's in what room. Keep the
        // current class's own room visible (passed-through prop excludes
        // this id from the taken set).
        const taken = this.roomsTaken || new Set()
        const availableRooms = this.roomOptions.filter(r => !taken.has(r))
        const menuItems = [
          h('button', {
            attrs: { type: 'button' },
            style: itemStyle,
            on: { mousedown: (e) => { e.preventDefault(); this.onSelect('') } }
          }, 'Clear room'),
          ...availableRooms.map(r => h('button', {
            attrs: { type: 'button' },
            style: this.cls.room === r ? selectedItemStyle : itemStyle,
            on: { mousedown: (e) => { e.preventDefault(); this.onSelect(r) } }
          }, shortRoom(r))),
          h('button', {
            attrs: { type: 'button' },
            style: customItemStyle,
            on: { mousedown: (e) => { e.preventDefault(); this.onSelect('__custom__') } }
          }, 'Custom…')
        ]
        lines.push(h('div', { style: wrapStyle }, [
          h('button', {
            attrs: { type: 'button' },
            style: btnStyle,
            on: { click: this.toggleMenu, blur: () => { setTimeout(() => { this.menuOpen = false }, 150) } }
          }, [label, h('span', { style: chevStyle }, '▾')]),
          this.menuOpen ? h('div', { style: menuStyle }, menuItems) : null
        ]))
      }
      if (this.roomConflict) {
        const room = (this.cls.room || this.cls.defaultRoom || '').trim()
        lines.push(h('div', { class: 'cls-room-clash' }, `⚠ ${room} also used in this time slot`))
      }
    }
    if (!this.cls.room && this.cls.defaultRoom) {
      lines.push(h('div', { class: 'cls-room-default' }, this.cls.defaultRoom))
    }
    const effectiveRoom = (this.cls.room || this.cls.defaultRoom || '').trim()
    const studios = this.danceStudioRooms || new Set()
    const tracks = Array.isArray(this.cls.tracks) && this.cls.tracks.length ? this.cls.tracks : [this.cls.track]
    const isRehearsal = tracks.includes('rehearsal')
    if (effectiveRoom && studios.has(effectiveRoom) && !isRehearsal) {
      lines.push(h('label', {
        class: ['cls-accompanist-toggle', this.cls.hasAccompanist ? 'is-on' : '']
      }, [
        h('input', {
          attrs: { type: 'checkbox' },
          domProps: { checked: !!this.cls.hasAccompanist },
          on: { change: (e) => this.$emit('accompanist', { id: this.cls.id, hasAccompanist: e.target.checked }) }
        }),
        ' 🎹 Live piano'
      ]))
    } else if (this.cls.hasAccompanist) {
      lines.push(h('div', { class: 'cls-accompanist' }, '🎹 Live piano'))
    }
    return h('div', { class: 'cls-cell' }, lines)
  }
}

export default {
  mixins: [iabfAdminMixin],
  components: { ClassCell },
  data () {
    return {
      loading: true,
      classes: [],
      activeDay: 'tuesday',
      TRACK_LABEL,
      ROOM_OPTIONS,
      DANCE_STUDIO_ROOMS,
      ALL_DAYS,
      EDIT_TRACK_CHOICES,
      editorOpen: false,
      editorIsNew: false,
      editorSaving: false,
      editorDeleting: false,
      editorError: '',
      editor: emptyEditor(),
      copiedTeacher: '',
      selectedTeacher: '',
      copyFlash: false,
      copyPrivateFlash: false,
      copyTimesFlash: false,
      copyDescriptionsFlash: '',
      publishStatus: null,
      publishSaving: false
    }
  },
  computed: {
    daysWithClasses () {
      const seen = {}
      for (const c of this.classes) {
        if (!seen[c.day]) seen[c.day] = c.dayOrder
      }
      // Show every known day even if empty so admin can add to any.
      const all = ALL_DAYS.map(d => ({ key: d.key, label: d.label, dayOrder: DAY_ORDER[d.key] }))
      return all.sort((a, b) => a.dayOrder - b.dayOrder)
    },
    activeDayLabel () {
      return DAY_LABEL[this.activeDay] || this.activeDay
    },
    knownTeachers () {
      // Distinct teacher names across ALL days, sorted alphabetically.
      // Used as the autocomplete option list for the editor. Also includes
      // EXTRA_FACULTY — faculty who exist on the roster but haven't been
      // assigned to a class yet (so they still show up in autocomplete +
      // get a teacher edit link).
      const set = new Set(EXTRA_FACULTY)
      for (const c of this.classes) {
        const t = (c.teacher || '').trim()
        if (t) set.add(t)
      }
      return Array.from(set).sort((a, b) => a.localeCompare(b))
    },
    teacherList () {
      // For the per-teacher edit links: skip combos like "Sean, Mikey, Krystal"
      // — those aren't a real single teacher and the matching is fuzzy enough
      // that any individual in the combo will still see those classes via
      // their own link.
      return this.knownTeachers.filter(t => !t.includes(','))
    },
    editorTeachers () {
      // Editor stores teacher as a comma-separated string for backward
      // compat with existing classes; this computed gives the picker an
      // array view of the same value.
      return (this.editor.teacher || '').split(',').map(s => s.trim()).filter(Boolean)
    },
    editorRoomIsDanceStudio () {
      const r = (this.editor && this.editor.defaultRoom || '').trim()
      if (!DANCE_STUDIO_ROOMS.has(r)) return false
      // Rehearsals don't get a live-piano option even when they're in a
      // dance studio — the accompanist plays for taught classes.
      const tracks = (this.editor && this.editor.tracks) || []
      return !tracks.includes('rehearsal')
    },
    teachersForDay () {
      // Distinct teachers across all classes on the active day, including
      // comma combos split out into their individual names.
      const set = new Set()
      for (const c of this.classes) {
        if (c.day !== this.activeDay) continue
        const raw = (c.teacher || '').trim()
        if (!raw) continue
        for (const name of raw.split(',').map(s => s.trim()).filter(Boolean)) {
          set.add(name)
        }
      }
      return Array.from(set).sort((a, b) => {
        const al = a.split(/\s+/).pop()
        const bl = b.split(/\s+/).pop()
        return al.localeCompare(bl)
      })
    }
  },
  async created () {
    this.$store.commit('setLayout', 'iabf-layout')
    const snap = await db.collection(this.IABF.classes).get()
    this.classes = snap.docs.map(d => ({ id: d.id, ...d.data() }))
    this.loading = false
    // Publish state lives in iabf-config-2026/itinerary so it can be flipped
    // from this admin page without a redeploy. Defaults to false when the
    // doc doesn't exist yet.
    try {
      const cfg = await db.collection(this.IABF.config).doc('itinerary').get()
      this.publishStatus = !!(cfg.exists && cfg.data().published)
    } catch (e) {
      this.publishStatus = false
    }
  },
  watch: {
    // Switching days clears the teacher filter — teachers vary by day,
    // and a stale selection would just show an empty result.
    activeDay () { this.selectedTeacher = '' }
  },
  methods: {
    toggleEditorTeacher (name) {
      const set = new Set(this.editorTeachers)
      if (set.has(name)) set.delete(name); else set.add(name)
      this.editor.teacher = Array.from(set).join(', ')
    },
    teacherLinkFor (name) {
      return `${window.location.origin}/iabf/teacher-itinerary?teacher=${encodeURIComponent(name)}`
    },
    async togglePublish () {
      const next = !this.publishStatus
      this.publishSaving = true
      try {
        await db.collection(this.IABF.config).doc('itinerary')
          .set({ published: next, updatedAt: new Date() }, { merge: true })
        this.publishStatus = next
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.alert('Failed to update publish state: ' + (e.message || e))
      }
      this.publishSaving = false
    },
    async copyTimeLabels () {
      // Distinct display times for the active day, in chronological order,
      // one per line — same set of slots that appear in the leftmost column
      // of the day grid.
      const text = this.timeSlotsForDay(this.activeDay).join('\n')
      try {
        await navigator.clipboard.writeText(text)
        this.copyTimesFlash = true
        setTimeout(() => { this.copyTimesFlash = false }, 1800)
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.prompt('Copy this:', text)
      }
    },
    async copyTeacherDescriptions (teacher) {
      // The active day's classes for ONE teacher only — combos and solo
      // entries that match this exact name string. Includes classes where
      // this teacher appears as part of a comma-separated co-teach list,
      // so co-taught classes show up under each co-teacher's copy too.
      const day = this.activeDay
      const target = teacher.toLowerCase()
      const list = this.classes
        .filter(c => c.day === day)
        .filter(c => c.track !== 'all')
        .filter(c => {
          const t = (c.teacher || '').toLowerCase()
          if (!t) return false
          if (t === target) return true
          return t.split(',').map(s => s.trim()).includes(target)
        })
        .slice()
        .sort((a, b) => classSortKey(a).start - classSortKey(b).start)

      // First name only ("Sean Kelly" → "Sean") for the header.
      const firstName = teacher.split(',').map(s => s.trim()).filter(Boolean)[0].split(/\s+/)[0]

      const entries = list.map(c => {
        const title = (c.title || '').trim() || '(untitled)'
        const desc = (c.description || '').trim()
        return desc ? `${title}\n${desc}` : title
      })
      const text = `Dance with ${firstName}\n\n${entries.join('\n\n')}`

      try {
        await navigator.clipboard.writeText(text)
        this.copyDescriptionsFlash = teacher
        setTimeout(() => {
          if (this.copyDescriptionsFlash === teacher) this.copyDescriptionsFlash = ''
        }, 1800)
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.prompt('Copy this:', text)
      }
    },
    async copyPrivateLessons () {
      const days = ['tuesday', 'wednesday', 'thursday', 'friday']
      const dayLabels = { tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday' }
      const csv = (s) => { const v = String(s || ''); return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v }

      // For each day, build a list of {time, teacher} rows. Private-lesson
      // classes often pack multiple short slots into the notes field as a
      // comma list (e.g. "1-1:30, 1:30-2"); each entry becomes its own row.
      // Fall back to the class's timeLabel when notes doesn't list slots.
      const byDay = {}
      for (const day of days) {
        const rows = []
        for (const c of this.classes) {
          if (c.day !== day) continue
          if (!((c.title || '').toLowerCase().includes('private lesson'))) continue
          const teacher = (c.teacher || '').trim()
          const notes = (c.notes || '').trim()
          const subSlots = notes.split(',').map(s => s.trim()).filter(s => /^\d/.test(s) && s.includes('-'))
          const slots = subSlots.length > 0 ? subSlots : [(c.timeLabel || '').trim()]
          for (const slot of slots) {
            if (slot) rows.push({ time: slot, teacher })
          }
        }
        rows.sort((a, b) => parseSlotRange(a.time).start - parseSlotRange(b.time).start)
        byDay[day] = rows
      }

      // Two-row header so the merged day labels survive a paste into Sheets.
      const lines = []
      lines.push(days.flatMap(d => [csv(dayLabels[d]), '', '']).join(','))
      lines.push(days.flatMap(() => ['Time', 'Teacher', 'Dancer']).join(','))
      const maxRows = Math.max(0, ...days.map(d => byDay[d].length))
      for (let i = 0; i < maxRows; i++) {
        const row = []
        for (const day of days) {
          const r = byDay[day][i] || { time: '', teacher: '' }
          row.push(csv(r.time), csv(r.teacher), '')
        }
        lines.push(row.join(','))
      }
      const text = lines.join('\n')

      try {
        await navigator.clipboard.writeText(text)
        this.copyPrivateFlash = true
        setTimeout(() => { this.copyPrivateFlash = false }, 1800)
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.prompt('Copy this:', text)
      }
    },
    formatClassCellText (cls) {
      if (!cls) return ''
      const lines = []
      if (cls.title) lines.push(cls.title)
      if (cls.teacher) lines.push(cls.teacher)
      if (cls.hasAccompanist) lines.push('🎹 Live piano')
      if (cls.notes) lines.push(cls.notes)
      const room = (cls.room || cls.defaultRoom || '').trim()
      if (room) lines.push(`📍 ${room}`)
      return lines.join('\n')
    },
    async copyDaySchedule () {
      const day = this.activeDay
      const tracks = this.tracksForDay(day)
      const slots = this.timeSlotsForDay(day)
      const esc = (s) => String(s || '').replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]))
      // Plain-text payload is TSV. Sheets paste splits on tabs (columns)
      // and newlines (rows); for in-cell multi-line content we collapse
      // the newlines to " · " so each cell stays on one row of the
      // resulting Sheet.
      const tsv = (s) => String(s || '').replace(/\n/g, ' · ').replace(/\t/g, ' ')
      const trackLabels = tracks.map(t => TRACK_LABEL[t] || t)

      // Standalone table (no <h3>) so Sheets / Excel parse it cleanly when
      // the clipboard's text/html payload is honored.
      let html = '<table border="1" cellpadding="6" cellspacing="0" style="border-collapse:collapse;font-family:Georgia,serif;">'
      html += `<thead><tr style="background:#faf8f3;"><th>Time</th>${trackLabels.map(t => `<th>${esc(t)}</th>`).join('')}</tr></thead><tbody>`

      let text = ['Time', ...trackLabels].map(tsv).join('\t') + '\n'

      for (const slot of slots) {
        const cells = this.rowCells(day, slot)
        const isAll = cells.length > 0 && cells[0].isAll
        html += '<tr>'
        html += `<td style="font-weight:700;white-space:nowrap;"><b>${esc(slot)}</b></td>`
        // Cell line breaks: emit `<br>` for rich-text targets AND keep a
        // real `\n` after it. Combined with `white-space: pre-line` on the
        // cell, this survives Google Sheets (which strips `<br>` but keeps
        // the LF) and renders as a single break in Gmail / Docs.
        const brnl = (s) => esc(s).replace(/\n/g, '<br>\n')
        if (isAll) {
          const cls = cells[0].classes[0]
          const txt = this.formatClassCellText(cls)
          html += `<td colspan="${tracks.length}" style="background:#eef6f8;text-align:center;white-space:pre-line;">${brnl(txt)}</td>`
          // Pad the meal row out to the full width so columns stay aligned
          // when this falls through to the plain-text TSV path.
          const allCols = [tsv(slot), tsv(txt), ...new Array(tracks.length - 1).fill('')]
          text += allCols.join('\t') + '\n'
        } else {
          const cellTexts = []
          for (const cell of cells) {
            const stacked = cell.classes.map(c => this.formatClassCellText(c)).filter(Boolean).join('\n— — —\n')
            cellTexts.push(stacked)
            html += `<td style="text-align:center;vertical-align:top;white-space:pre-line;">${brnl(stacked)}</td>`
          }
          text += [tsv(slot), ...cellTexts.map(tsv)].join('\t') + '\n'
        }
        html += '</tr>'
      }
      html += '</tbody></table>'

      try {
        if (navigator.clipboard && navigator.clipboard.write && typeof ClipboardItem !== 'undefined') {
          await navigator.clipboard.write([new ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([text], { type: 'text/plain' })
          })])
        } else {
          await navigator.clipboard.writeText(text)
        }
        this.copyFlash = true
        setTimeout(() => { this.copyFlash = false }, 1800)
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.prompt('Copy this:', text)
      }
    },
    async copyTeacherLink (name) {
      const url = this.teacherLinkFor(name)
      try {
        await navigator.clipboard.writeText(url)
        this.copiedTeacher = name
        setTimeout(() => { if (this.copiedTeacher === name) this.copiedTeacher = '' }, 1800)
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.prompt('Copy this URL:', url)
      }
    },
    tracksForDay (day) {
      const order = ['rehearsal', 'begBallet', 'intBallet', 'contemp']
      const seen = new Set()
      for (const c of this.classes) {
        if (c.day !== day) continue
        if (c.track === 'all') continue
        const ts = Array.isArray(c.tracks) && c.tracks.length ? c.tracks : [c.track]
        for (const t of ts) seen.add(t)
      }
      return order.filter(t => seen.has(t))
    },
    teacherClassesForDay (day) {
      const name = this.selectedTeacher.toLowerCase()
      if (!name) return []
      return this.classes
        .filter(c => {
          if (c.day !== day) return false
          const t = (c.teacher || '').toLowerCase()
          if (!t) return false
          if (t === name) return true
          return t.split(',').map(s => s.trim()).includes(name)
        })
        .slice()
        .sort((a, b) => {
          const ka = classSortKey(a)
          const kb = classSortKey(b)
          if (ka.start !== kb.start) return ka.start - kb.start
          return kb.end - ka.end
        })
    },
    orphanedClassesForDay (day) {
      // A class is orphaned if it lives on this day but none of its tracks
      // are recognized — usually because the admin removed the only track
      // (e.g. saved an empty tracks[]) or it's still referencing one of the
      // retired room-named tracks (harris/roberson/etc).
      const known = new Set(['rehearsal', 'begBallet', 'intBallet', 'contemp', 'all'])
      return this.classes
        .filter(c => c.day === day)
        .filter(c => {
          const ts = Array.isArray(c.tracks) && c.tracks.length ? c.tracks : (c.track ? [c.track] : [])
          if (ts.length === 0) return true
          return ts.every(t => !known.has(t))
        })
        .slice()
        .sort((a, b) => {
          const ka = classSortKey(a)
          const kb = classSortKey(b)
          return ka.start - kb.start
        })
    },
    roomsTakenInRow (day, slot, excludeId) {
      // Set of effective rooms used by every OTHER class sharing this
      // day+timeLabel. The current class's own room is left in so the
      // user can still see it (and switch away from it).
      const taken = new Set()
      for (const c of this.classes) {
        if (c.day !== day || c.timeLabel !== slot) continue
        if (c.id === excludeId) continue
        if (c.track === 'all') continue
        const room = (c.room || '').trim()
        if (room) taken.add(room)
      }
      return taken
    },
    rowRoomConflicts (day, slot) {
      // Returns a Set of room names that appear on more than one class in
      // this row. All-row meal classes are skipped (they're a single doc
      // filling every column, not a real collision).
      const counts = new Map()
      for (const c of this.classes) {
        if (c.day !== day || c.timeLabel !== slot) continue
        if (c.track === 'all') continue
        const room = (c.room || c.defaultRoom || '').trim()
        if (!room) continue
        counts.set(room, (counts.get(room) || 0) + 1)
      }
      const dup = new Set()
      for (const [room, n] of counts) {
        if (n > 1) dup.add(room)
      }
      return dup
    },
    timeSlotsForDay (day) {
      // Each unique timeLabel becomes one slot. We pick the earliest start
      // time among classes sharing a label as the slot's start, and the
      // latest end as the slot's end (so longer-end labels sort above
      // shorter-end labels with the same start).
      const seen = new Map()
      for (const c of this.classes) {
        if (c.day !== day) continue
        const key = c.timeLabel || ''
        const k = classSortKey(c)
        const prev = seen.get(key)
        if (!prev) {
          seen.set(key, { start: k.start, end: k.end })
        } else {
          if (k.start < prev.start) prev.start = k.start
          if (k.end > prev.end) prev.end = k.end
        }
      }
      return Array.from(seen.entries())
        .sort((a, b) => {
          if (a[1].start !== b[1].start) return a[1].start - b[1].start
          return b[1].end - a[1].end  // longer first when starts match
        })
        .map(([slot]) => slot)
    },
    getClass (day, slot, track) {
      // Match by tracks[] if present, else fall back to primary track.
      return this.classes.find(c => {
        if (c.day !== day || c.timeLabel !== slot) return false
        if (Array.isArray(c.tracks) && c.tracks.length) return c.tracks.includes(track)
        return c.track === track
      })
    },
    getClassesAt (day, slot, track) {
      return this.classes.filter(c => {
        if (c.day !== day || c.timeLabel !== slot) return false
        if (Array.isArray(c.tracks) && c.tracks.length) return c.tracks.includes(track)
        return c.track === track
      })
    },
    isAllRow (day, slot) {
      return this.classes.some(c => c.day === day && c.timeLabel === slot && c.track === 'all')
    },
    rowCells (day, slot) {
      // One cell per track column. Multi-track and all-row classes are
      // duplicated in each track they belong to; the underlying doc is
      // the same, so editing any duplicate edits them all. A cell can
      // hold more than one class doc when alternative options share a
      // day+time+track — both are rendered stacked.
      const tracks = this.tracksForDay(day)
      const allCls = this.classes.find(c => c.day === day && c.timeLabel === slot && c.track === 'all')
      return tracks.map(t => {
        if (allCls) {
          return { key: `${allCls.id}-${t}`, classes: [allCls], isAll: true }
        }
        const classes = this.getClassesAt(day, slot, t)
        if (classes.length === 0) {
          return { key: `gap-${t}`, classes: [] }
        }
        return { key: `${t}-${classes.map(c => c.id).join('-')}`, classes }
      })
    },
    async saveRoom ({ id, room }) {
      const cls = this.classes.find(c => c.id === id)
      if (!cls) return
      try {
        await db.collection(this.IABF.classes).doc(id).update({ room })
        cls.room = room
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.alert('Save failed: ' + (e.message || e))
      }
    },
    async saveAccompanist ({ id, hasAccompanist }) {
      const cls = this.classes.find(c => c.id === id)
      if (!cls) return
      try {
        await db.collection(this.IABF.classes).doc(id).update({ hasAccompanist })
        this.$set(cls, 'hasAccompanist', hasAccompanist)
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.alert('Save failed: ' + (e.message || e))
      }
    },
    openEditor (cls) {
      // Hydrate the form from the clicked class.
      const tracks = Array.isArray(cls.tracks) && cls.tracks.length ? cls.tracks.slice() : (cls.track ? [cls.track] : [])
      this.editor = {
        id: cls.id,
        day: cls.day || this.activeDay,
        dayOrder: cls.dayOrder != null ? cls.dayOrder : DAY_ORDER[cls.day],
        timeLabel: cls.timeLabel || '',
        startTime: cls.startTime || '',
        endTime: cls.endTime || '',
        startTimeDisplay: format12h(cls.startTime || ''),
        endTimeDisplay: format12h(cls.endTime || ''),
        order: cls.order != null ? cls.order : 100,
        tracks: tracks,
        title: cls.title || '',
        teacher: cls.teacher || '',
        defaultRoom: cls.defaultRoom || '',
        hasAccompanist: !!cls.hasAccompanist,
        notes: cls.notes || '',
        description: cls.description || ''
      }
      this.editorIsNew = false
      this.editorError = ''
      this.editorOpen = true
    },
    openNewClass () {
      this.editor = {
        ...emptyEditor(),
        day: this.activeDay,
        dayOrder: DAY_ORDER[this.activeDay]
      }
      this.editorIsNew = true
      this.editorError = ''
      this.editorOpen = true
    },
    normalize12h (raw) {
      // Called on blur — reformat valid input to canonical "5:30 PM",
      // leave invalid input alone so the user can fix it without losing
      // what they typed.
      const canonical = parseTo24h(raw)
      return canonical ? format12h(canonical) : raw
    },
    toggleEditorTrack (key) {
      const set = new Set(this.editor.tracks || [])
      if (set.has(key)) set.delete(key); else set.add(key)
      // 'all' is exclusive with any specific column.
      if (key === 'all' && set.has('all')) {
        this.editor.tracks = ['all']
      } else if (set.has('all') && set.size > 1) {
        set.delete('all')
        this.editor.tracks = Array.from(set)
      } else {
        this.editor.tracks = Array.from(set)
      }
    },
    classDocId (e) {
      const primary = (e.tracks && e.tracks[0]) || 'all'
      return `${e.day}-${String(e.order).padStart(3, '0')}-${primary}`
    },
    async saveEditor () {
      this.editorError = ''
      if (!this.editor.title) { this.editorError = 'Title is required.'; return }
      if (!this.editor.timeLabel) { this.editorError = 'Time label is required.'; return }
      if (!this.editor.tracks || !this.editor.tracks.length) { this.editorError = 'Pick at least one track.'; return }
      this.editorSaving = true
      try {
        const e = this.editor
        const primary = e.tracks[0]
        // Convert friendly 12h display back to canonical 24h for storage.
        e.startTime = parseTo24h(e.startTimeDisplay) || e.startTime || ''
        e.endTime = parseTo24h(e.endTimeDisplay) || e.endTime || ''
        // Order is auto-derived from start time so the admin never has to
        // think about it. Minutes-from-midnight gives a natural 0..1440
        // sort key; absent a start time, fall back to the existing value.
        const startMin = parse24h(e.startTime)
        const autoOrder = startMin !== null ? startMin : (Number(e.order) || 100)
        e.order = autoOrder
        const payload = {
          day: e.day,
          dayOrder: DAY_ORDER[e.day],
          timeLabel: (e.timeLabel || '').trim(),
          startTime: (e.startTime || '').trim(),
          endTime: (e.endTime || '').trim(),
          track: primary,
          tracks: e.tracks.slice(),
          trackLabel: TRACK_LABEL[primary] || primary,
          trackLabels: e.tracks.map(t => TRACK_LABEL[t] || t),
          title: (e.title || '').trim(),
          teacher: (e.teacher || '').trim(),
          notes: (e.notes || '').trim(),
          description: (e.description || '').trim(),
          defaultRoom: (e.defaultRoom || '').trim(),
          hasAccompanist: !!e.hasAccompanist && DANCE_STUDIO_ROOMS.has((e.defaultRoom || '').trim()) && !(e.tracks || []).includes('rehearsal'),
          order: autoOrder,
          updatedAt: new Date()
        }
        if (this.editorIsNew) {
          // Generate a fresh doc id; if it collides with an existing one
          // (same day/order/primary track), bump the order.
          let docId = this.classDocId(e)
          while (this.classes.some(c => c.id === docId)) {
            payload.order += 1
            e.order = payload.order
            docId = this.classDocId(e)
          }
          payload.room = ''
          await db.collection(this.IABF.classes).doc(docId).set(payload)
          this.classes.push({ id: docId, ...payload })
        } else {
          // Edits may change the primary track or start time (both feed
          // the doc id). If so, write to a new id and delete the old —
          // but bump the order until the new id is unique, so we never
          // silently overwrite a different class that happens to share
          // the same auto-derived id.
          const oldId = e.id
          let newId = this.classDocId(e)
          const existing = this.classes.find(c => c.id === oldId)
          if (newId !== oldId) {
            while (newId !== oldId && this.classes.some(c => c.id === newId)) {
              payload.order += 1
              e.order = payload.order
              newId = this.classDocId(e)
            }
            payload.room = (existing && existing.room) || ''
            await db.collection(this.IABF.classes).doc(newId).set(payload)
            await db.collection(this.IABF.classes).doc(oldId).delete()
            this.classes = this.classes.filter(c => c.id !== oldId)
            this.classes.push({ id: newId, ...payload })
          } else {
            await db.collection(this.IABF.classes).doc(oldId).update(payload)
            Object.assign(existing, payload)
          }
        }
        this.editorOpen = false
      } catch (err) {
        console.error(err)
        this.editorError = err.message || 'Save failed.'
      }
      this.editorSaving = false
    },
    async confirmDelete () {
      if (!this.editor.id) return
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Delete class "${this.editor.title}"? This can't be undone.`)) return
      this.editorDeleting = true
      try {
        await db.collection(this.IABF.classes).doc(this.editor.id).delete()
        this.classes = this.classes.filter(c => c.id !== this.editor.id)
        this.editorOpen = false
      } catch (e) {
        this.editorError = e.message || 'Delete failed.'
      }
      this.editorDeleting = false
    }
  }
}
</script>

<style scoped>
.itinerary-admin { max-width: 1200px; padding-bottom: 60px; }
.page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; flex-wrap: wrap; gap: 12px; }
h2 { font-family: Quattrocento, Georgia, serif; font-weight: 400; font-size: 28px; margin: 0; }
.muted { color: #888; font-style: italic; font-size: 14px; }
.pad-y { padding: 30px 0; }
.day-tabs { margin-top: 12px; margin-bottom: 12px; }
.itin-table { width: 100%; border-collapse: collapse; background: #fff; }
.itin-table thead th { background: #faf8f3; padding: 10px 12px; font-family: Quattrocento, Georgia, serif; font-weight: 700; font-size: 14px; text-align: center; border-bottom: 2px solid #f6b252; }
.itin-table thead th.time-col { width: 90px; }
.itin-table tbody td { border-bottom: 1px solid #ece9e2; padding: 10px 12px; vertical-align: top; font-family: 'Fanwood Text', Georgia, serif; font-size: 14px; }
.time-cell { font-weight: 700; font-family: Quattrocento, Georgia, serif; color: #1a1a1a; width: 90px; white-space: nowrap; background: #fef9ec; }
.all-cell { background: #eef6f8; text-align: center; }
.class-multi { background: #fef9ec; }
.cls-cell { display: flex; flex-direction: column; gap: 4px; text-align: center; }
.cls-title { font-weight: 700; font-family: Quattrocento, Georgia, serif; font-size: 14px; color: #1a1a1a; }
.cls-teacher { font-size: 13px; color: #555; }
.cls-notes { font-size: 12px; color: #888; font-style: italic; }
.cls-accompanist { font-size: 12px; color: #1a4452; font-weight: 700; }
.cls-accompanist-toggle { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: #666; cursor: pointer; margin-top: 4px; padding: 2px 6px; border-radius: 4px; border: 1px dashed #ccc; }
.cls-accompanist-toggle.is-on { color: #1a4452; font-weight: 700; background: #eef6f8; border-color: #267E7D; border-style: solid; }
.form-check { display: inline-flex; align-items: center; gap: 8px; font-family: 'Fanwood Text', Georgia, serif; font-size: 14px; cursor: pointer; }
.cls-room-row { display: flex; gap: 4px; align-items: center; justify-content: center; }
.cls-room-input { width: 100%; padding: 4px 6px; border: 1px solid #ccc; border-radius: 4px; font-size: 13px; font-family: 'Fanwood Text', Georgia, serif; background: #fff; text-align: center; }
.cls-room-x { background: transparent; border: none; cursor: pointer; font-size: 18px; color: #888; padding: 0 4px; }
.cls-room-default { font-size: 11px; color: #aaa; font-style: italic; }
.cls-room-conflict { border-color: #c62828 !important; background: #fff5f5; color: #c62828 !important; }
.cls-room-clash { font-size: 11px; color: #c62828; font-style: italic; margin-top: 2px; }

.cls-room-wrap { position: relative; }
.cls-room-btn { width: 100%; padding: 4px 22px 4px 6px; border: 1px solid #ccc; border-radius: 4px; font-size: 13px; font-family: 'Fanwood Text', Georgia, serif; background: #fff; text-align: center; cursor: pointer; position: relative; color: #555; }
.cls-room-btn:hover { background: #f7f3e9; }
.cls-room-btn.cls-room-set { border-color: #388b9d; color: #1a4452; font-weight: 700; }
.cls-room-chev { position: absolute; right: 6px; top: 50%; transform: translateY(-50%); color: #888; font-size: 10px; }
.cls-room-menu { position: absolute; top: 100%; left: 0; right: 0; margin-top: 2px; background: #fff; border: 1px solid #ccc; border-radius: 4px; box-shadow: 0 4px 12px rgba(0,0,0,0.08); z-index: 10; max-height: 280px; overflow-y: auto; }
.cls-room-menu-item { display: block; width: 100%; padding: 6px 10px; background: transparent; border: none; text-align: center; font-family: 'Fanwood Text', Georgia, serif; font-size: 13px; color: #1a1a1a; cursor: pointer; }
.cls-room-menu-item:hover { background: #f5efe0; }
.cls-room-menu-item.is-selected { background: #eef6f8; font-weight: 700; color: #1a4452; }
.cls-room-menu-custom { border-top: 1px solid #eee; font-style: italic; }
.stack-item { border-top: 1px dashed #ddd; padding-top: 8px; margin-top: 8px; }

.publish-bar { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin: 12px 0; padding: 10px 14px; background: #fff4e0; border: 1px solid #f4c63d; border-radius: 6px; font-family: 'Fanwood Text', Georgia, serif; }
.publish-bar.is-published { background: #e8f5e9; border-color: #66bb6a; }
.publish-status { display: flex; align-items: center; gap: 10px; }
.publish-dot { width: 10px; height: 10px; border-radius: 50%; background: #c62828; }
.publish-bar.is-published .publish-dot { background: #2e7d32; }
.publish-label { font-size: 14px; color: #1a1a1a; font-weight: 700; }

.desc-menu { background: #fff; min-width: 240px; max-width: 320px; padding: 8px 0; box-shadow: 0 4px 12px rgba(0,0,0,0.08); border-radius: 6px; }
.desc-menu-hint { padding: 4px 14px 8px; font-family: 'Fanwood Text', Georgia, serif; font-size: 12px; color: #777; font-style: italic; }
.desc-menu-item { display: flex; justify-content: space-between; align-items: center; gap: 10px; width: 100%; padding: 8px 14px; background: transparent; border: none; text-align: left; font-family: 'Fanwood Text', Georgia, serif; font-size: 14px; color: #1a1a1a; cursor: pointer; }
.desc-menu-item:hover { background: #f5efe0; }
.desc-menu-flash { color: #2e7d32; font-size: 12px; font-weight: 700; }
.desc-menu-empty { padding: 12px 14px; color: #999; font-style: italic; font-size: 13px; }

.teacher-filter { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; margin: 8px 0 12px; }
.filter-label { font-family: 'Fanwood Text', Georgia, serif; font-size: 13px; color: #777; margin-right: 4px; }
.teacher-chip { background: #fff; border: 1px solid #ddd; border-radius: 999px; padding: 5px 12px; font-family: 'Fanwood Text', Georgia, serif; font-size: 13px; color: #1a1a1a; cursor: pointer; transition: all 0.12s; }
.teacher-chip:hover { background: #f5f3ed; }
.teacher-chip.active { background: #267E7D; color: #fff; border-color: #267E7D; font-weight: 700; }

.teacher-column { padding: 8px 0 20px; }
.teacher-class-list { display: flex; flex-direction: column; gap: 12px; }
.teacher-class-row { display: flex; gap: 16px; align-items: flex-start; padding: 12px 14px; background: #fff; border: 1px solid #ece5d2; border-radius: 6px; }
.teacher-class-time { font-family: Quattrocento, Georgia, serif; font-weight: 700; color: #1a1a1a; font-size: 14px; min-width: 100px; padding-top: 4px; white-space: nowrap; }
.teacher-class-card { flex: 1; }
.teacher-class-card .cls-cell { max-width: 360px; margin: 0 auto; }

.teacher-links-panel { margin: 8px 0 12px; background: #fbfaf6; border: 1px solid #eee5d2; border-radius: 6px; }
.teacher-links-header { font-family: Quattrocento, Georgia, serif; font-weight: 700; font-size: 14px; color: #1a1a1a; }
.teacher-links-hint { font-size: 13px; margin-bottom: 12px; }
.teacher-link-row { display: flex; align-items: center; gap: 10px; padding: 6px 0; border-bottom: 1px solid #f0eadd; font-family: 'Fanwood Text', Georgia, serif; font-size: 13px; }
.teacher-link-row:last-child { border-bottom: none; }
.teacher-link-name { min-width: 160px; font-weight: 700; color: #1a1a1a; }
.teacher-link-url { flex: 1; font-family: monospace; font-size: 11px; color: #555; background: #fff; padding: 4px 8px; border-radius: 3px; border: 1px solid #ece5d2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.itin-table tbody td { text-align: center; }

.orphan-panel { margin-top: 16px; padding: 12px 14px; background: #fff8e1; border: 1px solid #f4c63d; border-radius: 6px; }
.orphan-header { font-family: Quattrocento, Georgia, serif; font-weight: 700; font-size: 14px; color: #8a6d00; }
.orphan-hint { font-size: 12px; color: #8a6d00; font-style: italic; margin-bottom: 8px; }
.orphan-table { width: 100%; border-collapse: collapse; background: #fff; }
.orphan-table td { padding: 6px 10px; font-family: 'Fanwood Text', Georgia, serif; font-size: 13px; border-bottom: 1px solid #f0e6c4; }
.orphan-row { cursor: pointer; }
.orphan-row:hover { background: #fffbe8; }
.orphan-time { font-weight: 700; width: 90px; }
.orphan-title { font-weight: 700; }
.orphan-teacher { color: #555; }
.orphan-tracks { font-size: 11px; }
.small { font-size: 12px; }

.day-actions { margin-bottom: 12px; }
.cls-title-btn { background: transparent; border: none; cursor: pointer; padding: 0; text-align: center; font-family: Quattrocento, Georgia, serif; font-weight: 700; font-size: 14px; color: #1a1a1a; width: 100%; }
.cls-title-btn:hover { color: #388b9d; }
.cls-edit-pencil { opacity: 0; font-size: 11px; color: #388b9d; margin-left: 4px; transition: opacity 0.15s; }
.cls-title-btn:hover .cls-edit-pencil { opacity: 1; }

.form-grid { display: flex; flex-direction: column; gap: 12px; }
.form-row { display: flex; flex-direction: column; gap: 4px; }
.form-cols { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 12px; }
.form-lab { font-family: 'Fanwood Text', Georgia, serif; font-weight: 700; font-size: 13px; color: #555; }
.form-input { width: 100%; padding: 7px 10px; border: 1px solid #ccc; border-radius: 4px; font-family: 'Fanwood Text', Georgia, serif; font-size: 14px; background: #fff; }
.form-input:focus { outline: none; border-color: #388b9d; }
.form-textarea { resize: vertical; min-height: 80px; font-family: 'Fanwood Text', Georgia, serif; }
.form-hint { font-size: 11px; color: #888; margin: 0; font-style: italic; }
.track-checks { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 6px 14px; padding: 8px 12px; background: #faf8f3; border-radius: 6px; }
.track-check { display: flex; align-items: center; gap: 6px; font-size: 13px; color: #1a1a1a; cursor: pointer; }
.track-check input { cursor: pointer; }

.teacher-picker { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 6px 14px; padding: 8px 12px; background: #faf8f3; border-radius: 6px; }
.teacher-pick-chip { display: flex; align-items: center; gap: 6px; font-size: 13px; color: #1a1a1a; cursor: pointer; padding: 4px 6px; border-radius: 4px; }
.teacher-pick-chip.active { background: #eef6f8; color: #1a4452; font-weight: 700; }
.teacher-pick-chip input { cursor: pointer; }
</style>
